import { useEffect, useRef } from "react";

import { usePusherChannelManager } from "./use-pusher-channel-manager";

const DEFAULT_THROTTLE_MS = 2000; // 2 seconds default throttle

/**
 * Generic props for the usePusherEvent hook
 * @template T Type of the event data
 */
interface UsePusherEventProps<T> {
  /** The channel ID to subscribe to */
  channelId?: string;
  
  /** The event type to listen for */
  eventType: string;
  
  /** Callback function called when an event is received */
  onEvent: (data: T) => void;
  
  /** Optional throttle time in ms (default: 2000ms) */
  throttleMs?: number;
  
  /** Optional flag to enable/disable subscription */
  enabled?: boolean;
}

/**
 * A generic hook for subscribing to Pusher events with throttling
 * @template T Type of the event data
 */
export function usePusherEvent<T>({
  channelId,
  eventType,
  onEvent,
  throttleMs = DEFAULT_THROTTLE_MS,
  enabled = !!channelId,
}: UsePusherEventProps<T>): void {
  const { subscribe, unsubscribe } = usePusherChannelManager();
  const lastEventTimeRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestDataRef = useRef<T | null>(null);
  const isMountedRef = useRef<boolean>(true);
  
  // Store the callback in a ref to avoid effect re-runs
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;
    
    // Effect running
    
    // If subscription is disabled or missing dependencies, do nothing
    if (!enabled || !channelId) {
      // Skipping subscription
      return undefined;
    }
    
    // Store the throttled callback reference so we can unbind it later
    let throttledCallback: ((data: T) => void) | null = null;
    
    // Function to throttle event callbacks
    throttledCallback = (data: T) => {
      // Event received
      
      // Only process if component is still mounted
      if (!isMountedRef.current) {
        // Component unmounted, ignoring event
        return;
      }
      
      const now = Date.now();
      
      // Save the latest data
      latestDataRef.current = data;
      
      // If enough time has passed since last callback, execute immediately
      if (now - lastEventTimeRef.current >= throttleMs) {
        lastEventTimeRef.current = now;
        // Executing callback immediately
        onEventRef.current(data);
      } else if (!timeoutRef.current) {
        // Otherwise, schedule a callback for later with the latest data
        const remainingTime = throttleMs - (now - lastEventTimeRef.current);
        // Throttling - scheduling callback
        timeoutRef.current = setTimeout(() => {
          // Check again if component is still mounted
          if (!isMountedRef.current) {
            // Component unmounted during timeout
            return;
          }
          
          lastEventTimeRef.current = Date.now();
          if (latestDataRef.current !== null) {
            // Executing throttled callback
            onEventRef.current(latestDataRef.current as T);
          }
          timeoutRef.current = null;
        }, remainingTime);
      } else {
        // Event throttled - timeout already scheduled
      }
      // If a timeout is already scheduled, do nothing 
      // (the scheduled timeout will use the latest data when it executes)
    };
    
    try {
      // Subscribe to the channel using the channel manager
      // Subscribing to channel
      const channel = subscribe(channelId);
      
      if (!channel) {
        // Failed to subscribe to channel
        return undefined;
      }
      
      // Bind to the specified event type
      // Binding to event
      channel.bind(eventType, throttledCallback);
      
      // Subscription successful
      
      // Clean up on unmount
      return () => {
        // Cleanup starting
        
        try {
          // Mark component as unmounted
          isMountedRef.current = false;
          
          // Clear any pending timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          
          // Unbind only this specific callback
          if (throttledCallback) {
            channel.unbind(eventType, throttledCallback);
          }
          
          // Unsubscribe using the channel manager (with ref counting)
          unsubscribe(channelId);
          
          // Cleanup successful
        } catch {
          // Cleanup error
        }
      };
    } catch {
      // Subscription setup error
      return undefined;
    }
  }, [subscribe, unsubscribe, channelId, eventType, throttleMs, enabled]);
}